// -----------------------------------------------------------------------------
// EditProfileForm.jsx – multi-étapes avec upload photo, correction alert & rafraîchissement
// -----------------------------------------------------------------------------
import React, { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  styled,
} from "@mui/material"
import { Formik, Form, Field, FieldArray } from "formik"
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Check,
} from "@mui/icons-material"
import PhoneInput from "react-phone-input-2"
import "react-phone-input-2/lib/style.css"

import LocalisationAutocomplete from "./LocalisationAutocomplete"
import PhotoUpload from "./PhotoUpload"
import {
  updateProfile,
  fetchProfilesOfCurrentUser,
  clearError,
} from "../../redux/profilesSlice"

const NAVY = "#2F4E8B"
const NAVY_LIGHT = "#4a6bad"
const OUTLINE = "#D4D9E0"
const LABEL_GREY = "#4F5565"
const LINE_GREY = "#C6CDD6"

const CustomTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    transition: "all .25s ease",
    "& fieldset": { borderColor: OUTLINE },
    "&:hover fieldset": { borderColor: NAVY_LIGHT },
    "&.Mui-focused fieldset": { borderColor: NAVY_LIGHT },
  },
  "& .MuiInputBase-input": { fontSize: 14 },
}))

const SectionPaper = ({ children, sx }) => (
  <Paper
    elevation={4}
    sx={{
      p: 3,
      mb: 4,
      borderRadius: 3,
      background:
        "linear-gradient(135deg, #FFFFFF 0%, #F6F9FC 60%, #E9F0F9 100%)",
      ...sx,
    }}
  >
    {children}
  </Paper>
)

const EditConnector = styled(StepConnector)({
  top: 14,
  "& .MuiStepConnector-line": {
    borderColor: LINE_GREY,
    borderTopWidth: 2,
    borderRadius: 1,
  },
})

const StepIconRoot = styled("div")(({ ownerState }) => ({
  zIndex: 1,
  width: 28,
  height: 28,
  borderRadius: "50%",
  border: `2px solid ${NAVY}`,
  backgroundColor:
    ownerState.completed || ownerState.active ? NAVY : "#FFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: ownerState.completed || ownerState.active ? "#FFF" : NAVY,
  fontWeight: 700,
  fontSize: 13,
}))
function StepIconComponent(props) {
  const { active, completed, icon } = props
  return (
    <StepIconRoot ownerState={{ active, completed }}>
      {completed ? <Check fontSize="small" /> : icon}
    </StepIconRoot>
  )
}

const steps = [
  "Infos Générales",
  "Compétences & Formations",
  "Préférences Travail",
  "Expériences",
  "Télécharger CV",
]
const stepNumbers = ["01", "02", "03", "04", "05"]

export default function EditProfileForm({ profile, onClose, onUpdate }) {
  const dispatch = useDispatch()
  const { loading } = useSelector((s) => s.profiles)

  const [activeStep, setActiveStep] = useState(0)
  const [cvFile, setCvFile] = useState(null)
  const [fileError, setFileError] = useState("")
  const [imageFile, setImageFile] = useState(null)
  const [imageError, setImageError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [apiError, setApiError] = useState("")
  const [profileVisible, setProfileVisible] = useState(profile.profileVisible)

  const initialValues = {
    nom: profile.nom || "",
    poste: profile.poste || "",
    tarif: profile.tarif || "",
    localisation: profile.localisation || "",
    competences: profile.competences || "",
    formations: profile.formations || "",
    certifications: profile.certifications || "",
    linkedin_url: profile.linkedin_url || "",
    mode_travail: profile.mode_travail || "",
    temps_travail: profile.temps_travail || "",
    phone: profile.phone || "",
    email: profile.email || "",
    disponibilite: profile.disponibilite || false,
    visibleContact: profile.visibleContact || "phone",
    experiences:
      profile.experiences?.length
        ? profile.experiences
        : [
            {
              title: "",
              company: "",
              startDate: "",
              endDate: "",
              location: "",
              description: "",
            },
          ],
  }

  const handleImageChange = (e) => {
    const f = e.target.files[0]
    if (f && (f.size > 5 * 1024 * 1024 || !f.type.startsWith("image/"))) {
      setImageError("Image ≤ 5 Mo")
      setImageFile(null)
      return
    }
    setImageError("")
    setImageFile(f)
  }

  const handleFileChange = (e) => {
    const f = e.target.files[0]
    if (f && f.size > 5 * 1024 * 1024) {
      setFileError("PDF ≤ 5 Mo")
      setCvFile(null)
    } else {
      setFileError("")
      setCvFile(f)
    }
  }

  const handleSubmit = async (values) => {
    setApiError("")
    dispatch(clearError())
    try {
      let payload = { ...values, profileVisible }
      const data = new FormData()
      data.append("profileData", JSON.stringify(payload))
      if (cvFile) data.append("cv", cvFile)
      if (imageFile) data.append("image", imageFile)
      const updatedProfile = await dispatch(
        updateProfile({ id: profile.id, profileData: data })
      ).unwrap()

      setSuccessMessage("Profil mis à jour !")
      setTimeout(() => setSuccessMessage(""), 4000)

      await dispatch(fetchProfilesOfCurrentUser()).unwrap()

      onUpdate?.(updatedProfile)
      onClose()
    } catch (err) {
      setApiError(
        err?.message ||
          "Une erreur inattendue est survenue lors de la mise à jour."
      )
    }
  }

  const handleDialogClose = (_, reason) => {
    if (reason === "backdropClick") return
    onClose()
  }

  return (
    <Dialog
      open
      maxWidth="md"
      fullWidth
      onClose={handleDialogClose}
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ textAlign: "center", position: "relative", py: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>
          Modifier Profil
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 8, right: 8, color: NAVY }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          pb: 4,
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-track": {
            background: "#F6F9FC",
            borderRadius: 4,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: NAVY,
            borderRadius: 4,
          },
          scrollbarColor: `${NAVY} #F6F9FC`,
          scrollbarWidth: "thin",
        }}
      >
        <PhotoUpload
          imageFile={imageFile}
          handleImageChange={handleImageChange}
          imageError={imageError}
          existingUrl={
            profile.localImagePath &&
            profile.localImagePath !== "Non spécifié"
              ? profile.localImagePath
              : ""
          }
        />

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          connector={<EditConnector />}
          sx={{ mb: 4 }}
        >
          {steps.map((label, idx) => (
            <Step key={idx}>
              <StepLabel
                StepIconComponent={StepIconComponent}
                StepIconProps={{ icon: stepNumbers[idx] }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {({
            values,
            handleChange,
            setFieldValue,
            submitForm,
            isSubmitting,
          }) => (
            <Form>
              {/* Étape 0 */}
              {activeStep === 0 && (
                <SectionPaper>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: NAVY, fontWeight: 700 }}
                  >
                    Informations Générales
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={CustomTextField}
                        name="nom"
                        label="Nom"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={CustomTextField}
                        name="poste"
                        label="Poste"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={CustomTextField}
                        name="tarif"
                        label="Tarif (€)"
                        type="number"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <LocalisationAutocomplete
                        value={values.localisation}
                        onChange={(v) =>
                          setFieldValue("localisation", v)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <PhoneInput
                        country="fr"
                        value={values.phone}
                        onChange={(val) =>
                          setFieldValue("phone", val)
                        }
                        inputStyle={{
                          width: "100%",
                          borderRadius: 8,
                          borderColor: OUTLINE,
                          height: 56,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={CustomTextField}
                        name="email"
                        label="Email"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={profileVisible}
                            onChange={(e) =>
                              setProfileVisible(e.target.checked)
                            }
                            sx={{
                              color: NAVY,
                              "&.Mui-checked": { color: NAVY },
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{ fontWeight: 700, color: NAVY }}
                          >
                            Profil Visible
                          </Typography>
                        }
                      />
                    </Grid>
                  </Grid>
                </SectionPaper>
              )}
              {/* Étape 1 */}
              {activeStep === 1 && (
                <SectionPaper>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: NAVY, fontWeight: 700 }}
                  >
                    Compétences & Formations
                  </Typography>
                  <Grid container spacing={3}>
                    {[
                      { field: "competences", label: "Compétences" },
                      { field: "formations", label: "Formations" },
                      {
                        field: "certifications",
                        label: "Certifications",
                      },
                      { field: "linkedin_url", label: "LinkedIn URL" },
                    ].map(({ field, label }) => (
                      <Grid item xs={12} sm={6} key={field}>
                        <Field
                          as={CustomTextField}
                          name={field}
                          label={label}
                          fullWidth
                        />
                      </Grid>
                    ))}
                  </Grid>
                </SectionPaper>
              )}
              {/* Étape 2 */}
              {activeStep === 2 && (
                <SectionPaper>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: NAVY, fontWeight: 700 }}
                  >
                    Préférences de Travail
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Mode de Travail</InputLabel>
                        <Select
                          name="mode_travail"
                          value={values.mode_travail}
                          onChange={handleChange}
                        >
                          {["Flexible", "Remote", "Sur Site"].map((o) => (
                            <MenuItem key={o} value={o}>
                              {o}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Temps de Travail</InputLabel>
                        <Select
                          name="temps_travail"
                          value={values.temps_travail}
                          onChange={handleChange}
                        >
                          {["Temps partiel", "Temps plein"].map((o) => (
                            <MenuItem key={o} value={o}>
                              {o}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.disponibilite}
                            onChange={(e) =>
                              setFieldValue(
                                "disponibilite",
                                e.target.checked
                              )
                            }
                            sx={{
                              color: NAVY,
                              "&.Mui-checked": { color: NAVY },
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{ fontWeight: 700, color: NAVY }}
                          >
                            Disponible
                          </Typography>
                        }
                      />
                    </Grid>
                  </Grid>
                </SectionPaper>
              )}
              {/* Étape 3 */}
              {activeStep === 3 && (
                <SectionPaper>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: NAVY }}
                    >
                      Expériences
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      sx={{ background: NAVY, fontWeight: "bold" }}
                      onClick={() =>
                        setFieldValue("experiences", [
                          ...values.experiences,
                          {
                            title: "",
                            company: "",
                            startDate: "",
                            endDate: "",
                            location: "",
                            description: "",
                          },
                        ])
                      }
                    >
                      Ajouter
                    </Button>
                  </Box>
                  <FieldArray name="experiences">
                    {({ remove }) =>
                      values.experiences.map((exp, idx) => (
                        <Paper
                          key={idx}
                          variant="outlined"
                          sx={{
                            p: 3,
                            mb: 3,
                            borderRadius: 2,
                            background: "#F6F9FC",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 2,
                            }}
                          >
                            <Typography>Expérience {idx + 1}</Typography>
                            <IconButton
                              color="error"
                              disabled={values.experiences.length === 1}
                              onClick={() => remove(idx)}
                            >
                              <RemoveIcon />
                            </IconButton>
                          </Box>
                          <Grid container spacing={3}>
                            {["title", "company"].map((f) => (
                              <Grid item xs={12} sm={6} key={f}>
                                <Field
                                  as={CustomTextField}
                                  name={`experiences.${idx}.${f}`}
                                  label={
                                    f === "title" ? "Titre" : "Entreprise"
                                  }
                                  fullWidth
                                />
                              </Grid>
                            ))}
                            <Grid item xs={12} sm={6}>
                              <Field
                                as={CustomTextField}
                                type="date"
                                name={`experiences.${idx}.startDate`}
                                label="Date début"
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Field
                                as={CustomTextField}
                                type="date"
                                name={`experiences.${idx}.endDate`}
                                label="Date fin"
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <LocalisationAutocomplete
                                value={exp.location}
                                onChange={(v) =>
                                  setFieldValue(
                                    `experiences.${idx}.location`,
                                    v
                                  )
                                }
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Field
                                as={CustomTextField}
                                multiline
                                rows={3}
                                name={`experiences.${idx}.description`}
                                label="Description"
                                fullWidth
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      ))
                    }
                  </FieldArray>
                </SectionPaper>
              )}
              {/* Étape 4 */}
              {activeStep === 4 && (
                <SectionPaper>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: NAVY, fontWeight: 700 }}
                  >
                    Télécharger CV (optionnel)
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, color: "gray" }}>
                    PDF – 5 Mo max.
                  </Typography>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    sx={{ background: NAVY, mb: 2, fontWeight: "bold" }}
                  >
                    Choisir un fichier
                    <input type="file" hidden onChange={handleFileChange} />
                  </Button>
                  {cvFile && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {cvFile.name}
                    </Typography>
                  )}
                  {fileError && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      {fileError}
                    </Alert>
                  )}
                </SectionPaper>
              )}

              {apiError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {apiError}
                </Alert>
              )}
              {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {successMessage}
                </Alert>
              )}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 2,
                }}
              >
                {activeStep > 0 ? (
                  <Button
                    variant="outlined"
                    onClick={() => setActiveStep((s) => s - 1)}
                    sx={{
                      borderColor: OUTLINE,
                      color: LABEL_GREY,
                      textTransform: "none",
                      px: 4,
                      fontWeight: "bold",
                    }}
                  >
                    Précédent
                  </Button>
                ) : (
                  <Box />
                )}
                <Button
                  variant="contained"
                  onClick={() => {
                    if (activeStep === steps.length - 1) submitForm()
                    else setActiveStep((s) => s + 1)
                  }}
                  disabled={loading || isSubmitting}
                  sx={{
                    background: NAVY,
                    textTransform: "none",
                    px: 5,
                    fontWeight: "bold",
                    "&:hover": { background: NAVY_LIGHT },
                  }}
                >
                  {loading || isSubmitting ? (
                    <CircularProgress size={22} sx={{ color: "#FFF" }} />
                  ) : activeStep === steps.length - 1 ? (
                    "Modifier"
                  ) : (
                    "Suivant"
                  )}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
